import React from 'react';
import 'react-dates/initialize';

import {
  compose,
  withStateHandlers,
  withPropsOnChange,
  withHandlers,
  mapProps,
} from 'recompose';
import { DayPickerRangeController } from 'react-dates';
import moment from 'moment';

import withField from '../withField';
import withClickOutside from 'lib/withClickOutside';
import cx from 'classnames';
import Icon from 'components/Icon';
import Predefined from './PredefinedDates';

import './styles.global.css';
import { omit } from 'ramda';

import styles from './styles.css';

const Picker = compose(
  withClickOutside((props) => props.onHide()),
  mapProps(omit(['onHide', 'initialStartDate', 'initialEndDate']))
)(DayPickerRangeController);

const initialValues = {
  start: null,
  end: null,
};

const _tomorrow = moment().day(1);
const _today = moment();

// TODO: Add types
export const DatePicker = compose<any, any>(
  withStateHandlers(
    ({ value }) => ({
      value:
        (value && { start: moment(value.start), end: moment(value.end) }) ||
        initialValues,
      focused: 'startDate',
      isOpen: false,
    }),
    {
      openDatePicker: (s) => () => ({ ...s, isOpen: true }),
      closeDatePicker: (s) => () => ({ ...s, isOpen: false }),
      onFocusChange: (s) => (focused) => ({
        ...s,
        focused: !focused ? 'startDate' : focused,
      }),
      onChange:
        (s, { onChange }) =>
        ({ startDate: start, endDate: end }) => {
          if (onChange && start && end) {
            onChange({
              start: start.format('YYYY-MM-DD'),
              end: end.format('YYYY-MM-DD'),
            });
          }
          return { ...s, value: { start, end } };
        },
    }
  ),
  withPropsOnChange(['value'], ({ value }) => {
    return {
      label: {
        start: value.start && value.start.format('MMM D,YYYY'),
        end: value.end && value.end.format('MMM D,YYYY'),
        range:
          value.start &&
          value.end &&
          `${value.end.isSame(_today, 'day') ? 'Last ' : ''}
          ${value.start.to(value.end, true).replace(/^a /g, '')}`,
      },
    };
  }),
  withPropsOnChange(['limit'], ({ limit }) => ({
    limit: limit && moment().subtract(limit, 'days'),
  })),
  withPropsOnChange(
    ['children'],
    ({ children, openDatePicker }) =>
      children && {
        button: React.cloneElement(children, { onClick: openDatePicker }),
      }
  ),
  withHandlers({
    disableDays:
      ({ value, limit }) =>
      (day) =>
        day.isAfter(_today) || (limit && day.isBefore(limit)),
  })
)(
  ({
    label,
    value = initialValues,
    defaultValue = initialValues,
    focused,
    isOpen,
    align,
    onFocusChange,
    onChange,
    closeDatePicker,
    openDatePicker,
    disableDays,
    pickerStyles,
    button,
  }: any) => (
    <div className={cx(styles.root, isOpen && styles.open, styles[align])}>
      {button || (
        <button onClick={openDatePicker} className={styles.button}>
          <Icon name="date-range" />
          <div className={styles.title}>
            {label.range && <span className={styles.range}>{label.range}</span>}
            <span className={styles.dates}>
              {label.start} - {label.end}
            </span>
          </div>
          <Icon name="arrow-down" width={17} height={17} />
        </button>
      )}
      {isOpen && (
        <div className={cx(styles.picker)} style={pickerStyles}>
          <Picker
            hideKeyboardShortcutsPanel
            onHide={closeDatePicker}
            onDatesChange={onChange}
            startDate={value.start}
            initialStartDate={defaultValue.start}
            endDate={value.end}
            onFocusChange={onFocusChange}
            focusedInput={focused}
            initialEndDate={defaultValue.end}
            isOutsideRange={disableDays}
            minimumNights={0}
            renderCalendarInfo={() => (
              <Predefined
                onChange={onChange}
                closeDatePicker={closeDatePicker}
              />
            )}
          />
        </div>
      )}
    </div>
  )
);

export default withField(DatePicker);
